import React, { useState, useEffect, useRef } from 'react';
import { Logo, SearchIcon, ScaleIcon, LockIcon, BookIcon, CompassIcon, ClipboardCheckIcon, MenuIcon, XIcon } from './Icons';

const API_BASE = '/api';

const T = {
  en: {
    nav_features: 'Features', nav_pricing: 'Pricing', nav_security: 'Security', nav_signin: 'Sign In',
    lang_toggle: '中文',
    hero_tag: 'Contract Intelligence Platform',
    hero_h1a: 'How strong are ', hero_h1b: 'your', hero_h1c: ' clauses?',
    hero_desc: 'Benchmark your contract clauses against 338,000+ real SEC filings from Fortune 500 and listed companies. See exactly how your language compares — across US, Singapore, and China jurisdictions.',
    hero_sub: 'Paste any clause. Get a grade. See the most similar filings. Know where you stand — in seconds, not days.',
    search_btn: 'Search', search_loading: 'Searching...',
    search_cta: 'Sign up free to see full clause text and source citations.',
    proof_clauses: ['clauses', 'indexed'], proof_j: ['jurisdictions', 'US · SG · CN'],
    proof_cat: ['clause categories', 'classified'], proof_enc: ['encryption', 'at rest'],
    prob_tag: 'The benchmark that doesn\'t exist — until now',
    prob_q1: 'You know your clauses work. But how do they ', prob_qhl: 'actually compare', prob_q2: ' to what listed companies filed?',
    prob_p1: 'Every corporate lawyer has their trusted clause library. But no one has ever been able to benchmark that language against what Fortune 500 companies, US-listed Chinese tech firms, and Singapore-listed corporates actually filed with regulators.',
    prob_p2: 'Paste your indemnity clause. We\'ll show you the 5 most similar clauses from real SEC filings, tell you if your language is stronger or weaker than market, and cite the exact filing. No LLM guessing. No hallucination. Just data.',
    prob_btn: 'Benchmark your clause free',
    corpus_title: 'Corpus composition',
    ai_tag: 'The new reality',
    ai_h2: 'Your client already has access to loads of AI chats.',
    ai_p1a: 'Sophisticated clients are running your drafts through ChatGPT, Claude, Perplexity, Gemini, and every other AI tool on the market — ', ai_p1b: 'before they sign', ai_p1c: '.',
    ai_p2: "They'll spot language that differs from what's filed publicly. They'll ask why your indemnity cap is lower than the market standard. They'll ask why your governing law clause doesn't match the pattern in Fortune 500 cross-border deals.",
    ai_p3: "icontracts lets you check first — against the same public filings their AI is trained on. See what they'll see. Fix it before they ask.",
    ai_quote: "A tool for the lawyer who’d rather be three steps ahead than one step behind.",
    feat_tag: 'Platform',
    feat_h2: 'Twelve tools built for lawyers who work across borders',
    feat_desc: 'Powered by real commercial contracts aggregated from SEC EDGAR (US), Singapore Exchange (SGX), Hong Kong Exchange (HKEX), Shanghai Stock Exchange (SSE), and Shenzhen Stock Exchange (SZSE). Vectorized, classified, and cross-referenced across jurisdictions. Not AI-generated. Every result verifiable.',
    uc_tag: 'How lawyers use it',
    uc_h2: 'The benchmark no one else can give you',
    uc_desc: 'Real clauses from real regulatory filings — SEC EDGAR, SGX, HKEX, SSE, and SZSE. Every result citable and verifiable across US, Singapore, and China.',
    diff_tag: 'See It In Action',
    diff_h2a: 'Your clause vs. market standard — ', diff_h2b: 'differences highlighted instantly',
    diff_desc: 'Paste your clause, click Compare, and see exactly where your language diverges from 338,000+ real filed contracts. No guesswork. No hallucination. Just data.',
    diff_leg1: 'Only in your clause', diff_leg2: 'Only in matched clause', diff_leg3: 'Common language',
    diff_yours: 'Your Clause', diff_match: 'Best Match (SEC Filing)',
    diff_src: 'Source: SEC EDGAR filing. Comparison is for reference only — not legal advice.',
    diff_sub: 'Spot gaps in seconds that would take hours to find manually. Every comparison is backed by real filed contracts — not AI-generated text.',
    diff_btn: 'Try It Free →',
    tg_tag: 'Mobile Intelligence',
    tg_h2a: 'Just talk. Your AI assistant ', tg_h2b: 'listens, searches, and cites', tg_h2c: ' — in seconds.',
    tg_p1: 'Mid-meeting. Mid-negotiation. Step out, open Telegram, and just speak. Our bot transcribes your voice, searches 338,000+ clauses, and replies with graded results and source citations — before you\'re back in the room.',
    tg_p2: 'No typing. No laptop. No browser. Just talk to your phone like you\'re asking a senior partner — except this partner has read every SEC, SGX, and HKEX filing ever published.',
    tg_p3: 'Three dedicated bots. One for clause search. One for benchmarking and grading. One for jurisdiction comparison. Each one gives you the answer, the grade, and the citation — ready to quote in front of your client.',
    tg_plan: 'Included in the Max plan.',
    tg_tag2: 'Making every billable hour sharper.',
    priv_tag: 'Built for the individual lawyer',
    priv_h2: 'Your firm doesn\'t need to know.',
    priv_p1a: 'icontracts is a tool for ', priv_p1b: 'you', priv_p1c: ' — the individual lawyer — not your firm. We don\'t sell to law firms. We don\'t ask where you work. We don\'t want to know.',
    priv_p2a: 'Use your ', priv_p2b: 'personal email', priv_p2c: ' when you sign up. Your searches, saved clauses, and Vault files are yours alone — encrypted with a password only you hold. We cannot read your files even if we wanted to.',
    priv_quote: 'A tool that makes every billable hour sharper — quietly, privately, and on your own terms.',
    price_tag: 'Pricing', price_h2: 'Simple, transparent pricing',
    price_desc: 'No enterprise sales calls. Start free, upgrade when you\'re ready.',
    free_name: 'Free', free_sub: 'Try before you commit',
    free_f: ['Clause discovery search','Clause benchmarking','24 searches / month','Full scorecard','PDF reports','Encrypted vault'],
    free_btn: 'Get Started',
    mo_name: 'Plus Monthly', mo_sub: 'No commitment, cancel anytime',
    plus_f: ['Unlimited searches','Full contract scorecard','Side-by-side clause diff','PDF benchmark reports','Saved clause library','Encrypted vault (1,200 files)'],
    mo_btn: 'Start Plus',
    yr_name: 'Plus Annual', yr_badge: 'Best Value', yr_sub: 'S$360/year — save 67%',
    yr_f: ['Everything in Plus Monthly','Unlimited searches','Full contract scorecard','PDF reports & clause diff','Saved clause library','Encrypted vault (1,200 files)'],
    yr_btn: 'Start Plus Annual',
    max_name: 'Max', max_badge: 'Power User', max_sub: 'The full arsenal',
    max_f: ['Everything in Plus','IP cross-reference (USPTO)','3 Telegram AI bots','Deal structure templates','Clause alerts on new filings','Clause version tracking'],
    max_btn: 'Join Waitlist',
    price_note: 'All prices in Singapore Dollars (SGD) unless marked USD. Max tier includes IP cross-reference worth US$5,000/year alone.',
    sec_tag: 'Security', sec_h2: 'Built for lawyers who value privacy',
    sec_desc: 'Your research is confidential. We built every layer of icontracts.net to keep it that way.',
    sec_f: [['AES-256-GCM','Military-grade encryption. Each file has a unique key derived from your password.'],['No Tracking',"We don't track your searches. We don't sell data. We don't train AI on your queries."],['Your Keys Only','Encryption keys are derived from your password. Forget it, your files are gone. By design.']],
    ds_tag: 'Data Sources', ds_h3: 'Where our intelligence comes from',
    ds_desc: 'We aggregate contracts, announcements, and agreements from every source above. Then we vectorize, classify into 11 clause categories, tag by jurisdiction, and cross-reference across all exchanges — so you can compare how the same clause type is drafted under US law, Singapore law, and Chinese law. All publicly available. All verifiable. With a dedicated focus on corporate law.',
    cta_h2: 'Find out how your clauses really compare.',
    cta_desc: 'Paste any clause. Get an instant benchmark grade against 338,000+ real filings. Start free, no credit card required.',
    cta_btn: 'Create Free Account',
    ft_desc: 'Encrypted legal contract intelligence. Search and benchmark clauses against 338,000+ real commercial contracts.',
    ft_product: 'Product', ft_legal: 'Legal',
    ft_links: ['Features','Pricing','Security'], ft_legal_links: ['Terms of Service','Privacy Policy','Contact'],
    ft_solo: 'icontracts.net is built and operated by a solo founder. Support queries are answered within 48 hours. For billing changes, please use your Stripe customer portal — it\'s the fastest path.',
    ft_rights: 'icontracts.net. All rights reserved.',
    ft_disclaimer: 'Data sourced from publicly filed commercial contracts and regulatory disclosures. Not legal advice.',
    wl_tag: 'Max Waitlist', wl_h3: 'Early access to Max',
    wl_already: "You're already on the waitlist.", wl_added: "You're on the waitlist.",
    wl_ok_desc: "We'll email you the moment Max launches. Early access is reserved for those who subscribe to Max when it goes live.",
    wl_close: 'Close',
    wl_desc: 'The Max tier — including IP Cross-Reference (USPTO), Telegram AI bots, Deal Structure Templates, and Clause Alerts — is under active development. Join the waitlist and we\'ll contact you the moment it\'s ready. Early access is reserved for those who subscribe to Max when launched.',
    wl_name: 'Your Name', wl_name_ph: 'Your name',
    wl_email: 'Personal Email', wl_email_ph: 'name@gmail.com',
    wl_email_note: 'Please use your personal email, not your firm address. We don\'t want to know which firm you\'re from.',
    wl_submit: 'Join Waitlist', wl_submitting: 'Submitting...',
    wl_no_pay: 'No payment. No commitment. We email you when Max is ready.',
    wl_both: 'Please fill in both fields.',
    features_arr: [
      { title: 'Clause Benchmarking', desc: 'Paste your clause, get an instant grade. See exactly how your language compares against what Fortune 500 and listed companies actually filed. Stronger, weaker, or market standard — with the proof to back it up.', detail: 'A/B/C/D grading with source citations' },
      { title: 'Clause Discovery', desc: 'Search how real companies handle any clause type across US, Singapore, and China. Indemnity caps, non-competes, force majeure, governing law — from real filings, not templates.', detail: '338,000+ clauses across 3 jurisdictions' },
      { title: 'Full Contract Scorecard', desc: "Upload an entire contract. We split it into clauses, benchmark every one, and show you exactly what's strong, what's weak, and why — with specific provisions checked against market practice.", detail: 'Clause-by-clause grading with explanations' },
      { title: 'Side-by-Side Clause Diff', desc: 'See your clause next to the best match from the corpus — with differences highlighted in redline style. Know exactly what language is missing, added, or different from market standard.', detail: 'Visual redline comparison' },
      { title: 'Saved Clause Library', desc: 'Bookmark clauses, organize them in folders, and add personal notes. Build your own dynamic intelligence library over your career — your precedent bank that grows every time you use the platform.', detail: 'Personal precedent bank' },
      { title: 'Benchmark Reports (PDF)', desc: 'Download professional PDF reports with grades, assessments, strengths, weaknesses, and comparable clauses. Your reference document for client discussions and internal memos.', detail: 'Downloadable analysis reports' },
      { title: 'VIE Structure Library', desc: "Every VIE component from US-listed Chinese companies — Exclusive Service Agreements, Equity Pledges, Call Options, Powers of Attorney. The structures that unlock access to China's restricted sectors.", detail: 'Cross-border deal precedents' },
      { title: 'Governing Law Navigator', desc: 'For multi-layered cross-border deals, see how real filed transactions chose governing law and dispute resolution at each layer. Know whether SIAC, CIETAC, or AAA is the market choice.', detail: 'Jurisdiction intelligence' },
      { title: 'Deal Structure Templates', desc: 'Pre-built clause packages for common deal types — SG-CN joint ventures, VIE acquisitions, cross-border M&A. See how real deals structured every clause from governing law to termination.', detail: 'Full skeleton from real filings', anchor: 'feature-deal-templates' },
      { title: 'IP Cross-Reference (USPTO)', desc: 'Paste your IP indemnity, license, or tech-transfer clause and instantly surface related US patents. Know which technology areas carry highest third-party risk before you draft the representation. Powered by the full USPTO corpus.', detail: 'Clause-to-patent risk mapping', anchor: 'feature-ip-cross-reference' },
      { title: 'Clause Alerts & Version Tracking', desc: 'Create watch-lists on specific clause topics or keywords. When new filings hit the corpus with matching language, we surface them. Track how market drafting evolves in the areas that matter to your practice.', detail: 'Market intelligence, continuous', anchor: 'feature-clause-alerts' },
      { title: 'Encrypted Vault', desc: 'Store up to 1,200 contracts with AES-256-GCM encryption. Each file encrypted with a unique key derived from your password. Your research stays yours.', detail: 'Encrypted-at-rest file storage' },
    ],
    uc_arr: [
      { quote: "I pasted my indemnity clause and got a Grade A with 86% similarity to clauses in Fortune 500 SEC filings. Now I have the proof when my client asks 'is this market standard?'", type: 'Clause Benchmarking' },
      { quote: 'Found 12 VIE Exclusive Service Agreements from US-listed Chinese tech companies. Compared the call option structures side by side. Saved my team 3 days of EDGAR research.', type: 'China Market Access' },
      { quote: 'Opposing counsel insisted on CIETAC arbitration. I showed them that 72% of similar Singapore-China JVs in the SEC corpus used SIAC instead. They backed down.', type: 'Negotiation Leverage' },
      { quote: 'Needed to benchmark our limitation of liability clause against US market practice before a cross-border M&A closing. Got the answer in 30 seconds — Grade B, slightly below market.', type: 'US Capital Markets' },
      { quote: 'Checked how Singapore-governed contracts handle force majeure versus PRC-governed ones in the same deal structure. The difference in drafting approach was eye-opening.', type: 'Cross-Jurisdiction Comparison' },
      { quote: 'Partner asked for a \'market standard\' non-compete for a China country manager. I searched the corpus, found 3,400+ executive employment agreements, and drafted from the strongest examples.', type: 'Executive Employment' },
    ],
    ds_arr: [
      { exchange: 'SEC EDGAR', jurisdiction: 'United States', count: '338,000+', desc: 'Corporate contracts from US public company filings — 10-K, 10-Q, 20-F, S-1 exhibits' },
      { exchange: 'SGX', jurisdiction: 'Singapore', count: '', desc: 'Singapore Exchange corporate announcements, material contracts, and IPO prospectuses' },
      { exchange: 'HKEX', jurisdiction: 'Hong Kong', count: '', desc: 'Hong Kong Exchange corporate filings, listing documents, and cross-border agreements' },
      { exchange: 'SSE', jurisdiction: 'Shanghai', count: '', desc: 'Shanghai Stock Exchange publicly filed contracts and corporate disclosures' },
      { exchange: 'SZSE', jurisdiction: 'Shenzhen', count: '', desc: 'Shenzhen Stock Exchange filings — technology, manufacturing, and cross-border agreements' },
      { exchange: 'Public Records', jurisdiction: 'Multi-jurisdiction', count: '', desc: 'Regulatory disclosures, government gazette publications, and publicly accessible legal databases' },
    ],
    corpus_bars: [
      { label: 'Indemnification', count: '48,200+', pct: 82 },
      { label: 'Limitation of Liability', count: '41,600+', pct: 71 },
      { label: 'Governing Law', count: '38,900+', pct: 66 },
      { label: 'Termination', count: '35,100+', pct: 60 },
      { label: 'Confidentiality', count: '29,800+', pct: 51 },
      { label: 'Force Majeure', count: '12,400+', pct: 21 },
      { label: 'Dispute Resolution', count: '3,800+', pct: 7 },
    ],
    tg_voice1: '"How does my indemnity cap compare to market standard?"',
    tg_bot1a: 'Your indemnity clause scores ', tg_bot1b: 'Grade A', tg_bot1c: ' — 86% similarity to top SEC filings.',
    tg_bot1d: 'Most similar: ', tg_bot1e: 'Fortune 500 Supply Agreement, Exhibit 10.1', tg_bot1f: ' — identical cap structure at aggregate contract value.',
    tg_bot1g: 'Stronger than 78% of indemnity clauses in the corpus.',
    tg_voice2: '"Show me SIAC vs CIETAC for this deal structure"',
    tg_bot2a: 'For SG-CN JV structures, ', tg_bot2b: 'SIAC is used in 72%', tg_bot2c: ' of similar filings. CIETAC in 18%.',
    tg_bot2d: 'Sources: 3 SEC 20-F filings, 2 SGX announcements',
  },
  zh: {
    nav_features: '功能特点', nav_pricing: '价格方案', nav_security: '安全保障', nav_signin: '登录',
    lang_toggle: 'EN',
    hero_tag: '合同智能分析平台',
    hero_h1a: '您的合同条款', hero_h1b: '实力', hero_h1c: '几何？',
    hero_desc: '将您的合同条款与338,000+份来自财富500强及上市公司的真实SEC监管文件进行对标。精准了解您的合同语言在美国、新加坡和中国司法管辖区的市场地位。',
    hero_sub: '粘贴任意条款，获取评级，查看最相似的真实归档条款，秒级掌握您的市场位置。',
    search_btn: '搜索', search_loading: '搜索中…',
    search_cta: '免费注册，查看完整条款原文及来源引用。',
    proof_clauses: ['条款', '已索引'], proof_j: ['司法管辖区', '美国·新加坡·中国'],
    proof_cat: ['条款类别', '已分类'], proof_enc: ['静态', '加密标准'],
    prob_tag: '前所未有的条款对标基准',
    prob_q1: '您知道自己的条款行之有效。但与上市公司的实际归档内容相比，', prob_qhl: '差距究竟几何', prob_q2: '？',
    prob_p1: '每位企业律师都有自己信赖的条款库。但从未有人能够将这些条款语言与财富500强、美国上市中国科技公司及新加坡上市企业向监管机构实际归档的内容进行系统性对标。',
    prob_p2: '粘贴您的赔偿条款，我们将为您呈现真实SEC文件中最相似的5条条款，告知您的语言强于或弱于市场标准，并注明确切文件来源。无AI推测，无幻觉输出，只有数据。',
    prob_btn: '免费对标您的条款',
    corpus_title: '语料库构成',
    ai_tag: '全新现实',
    ai_h2: '您的客户已经掌握了大量AI工具。',
    ai_p1a: '精明的客户正在用ChatGPT、Claude、Perplexity、Gemini以及市面上所有AI工具审查您的草案——', ai_p1b: '在签字之前', ai_p1c: '。',
    ai_p2: '他们会发现与公开归档内容不一致的语言表述。他们会质疑为何您的赔偿上限低于市场标准。他们会追问为何您的准据法条款与财富500强跨境交易的惯例不符。',
    ai_p3: 'icontracts让您抢先一步——对照他们AI所参考的同一批公开文件进行自查。提前掌握他们将看到的内容，在他们发问之前完善草案。',
    ai_quote: '专为那些宁愿领先三步而非落后一步的律师打造。',
    feat_tag: '平台功能',
    feat_h2: '十二项工具，专为跨境执业律师打造',
    feat_desc: '数据来源于SEC EDGAR（美国）、新加坡交易所（SGX）、香港交易所（HKEX）、上海证券交易所（SSE）及深圳证券交易所（SZSE）的真实商业合同。经过向量化处理、分类标注，并跨司法管辖区交叉引用。非AI生成内容，每项结果均可溯源验证。',
    uc_tag: '律师使用场景',
    uc_h2: '独一无二的条款对标基准',
    uc_desc: '来自SEC EDGAR、SGX、HKEX、SSE及SZSE真实监管文件的原始条款，每项结果均可在美国、新加坡和中国的法律体系下引用核实。',
    diff_tag: '功能演示',
    diff_h2a: '您的条款 vs. 市场标准——', diff_h2b: '差异即时高亮显示',
    diff_desc: '粘贴您的条款，点击比较，即可精准定位您的语言与338,000+份真实归档合同的差异所在。无需猜测，无幻觉输出，只有数据。',
    diff_leg1: '仅见于您的条款', diff_leg2: '仅见于匹配条款', diff_leg3: '共有语言',
    diff_yours: '您的条款', diff_match: '最佳匹配（SEC文件）',
    diff_src: '来源：SEC EDGAR归档文件。比较结果仅供参考，不构成法律意见。',
    diff_sub: '数秒识别人工查找需数小时才能发现的差距。每项比较均基于真实归档合同，而非AI生成文本。',
    diff_btn: '免费试用 →',
    tg_tag: '移动端智能助手',
    tg_h2a: '直接开口说。您的AI助手', tg_h2b: '聆听、搜索、引用', tg_h2c: '——秒级响应。',
    tg_p1: '会议进行中，谈判关键时刻，走出房间，打开Telegram，直接开口。我们的机器人将语音转录，搜索338,000+条款，并在您返回会议室之前回复评级结果和来源引用。',
    tg_p2: '无需打字，无需笔记本电脑，无需浏览器。像向资深合伙人请教一样与手机交流——只不过这位合伙人已阅读过所有SEC、SGX和HKEX的公开文件。',
    tg_p3: '三个专属机器人：一个用于条款搜索，一个用于对标评级，一个用于司法管辖区比较。每个机器人均提供答案、评级和引用——随时可在客户面前援引。',
    tg_plan: '包含于Max方案。',
    tg_tag2: '让每一个计费小时更加锐利。',
    priv_tag: '专为个人律师打造',
    priv_h2: '您的律所无需知晓。',
    priv_p1a: 'icontracts是专属于', priv_p1b: '您', priv_p1c: '——个人律师——的工具，而非律所工具。我们不向律所销售，不询问您的就职单位，也无意获知。',
    priv_p2a: '注册时请使用', priv_p2b: '个人邮箱', priv_p2c: '。您的搜索记录、保存的条款和Vault文件完全属于您个人——以仅您持有的密码进行加密。即便我们有意，也无法读取您的文件。',
    priv_quote: '一款让每个计费小时更加锐利的工具——静默、私密、完全由您掌控。',
    price_tag: '价格方案', price_h2: '简单透明的定价',
    price_desc: '无需企业销售电话。免费开始，准备好后随时升级。',
    free_name: '免费版', free_sub: '先体验，再决定',
    free_f: ['条款发现搜索','条款对标','每月24次搜索','完整合同评分卡','PDF报告','加密保险库'],
    free_btn: '立即开始',
    mo_name: 'Plus月付', mo_sub: '无承诺，随时取消',
    plus_f: ['无限次搜索','完整合同评分卡','条款并排对比','PDF对标报告','已保存条款库','加密保险库（1,200份文件）'],
    mo_btn: '开通Plus',
    yr_name: 'Plus年付', yr_badge: '最优惠', yr_sub: 'S$360/年——节省67%',
    yr_f: ['包含Plus月付全部功能','无限次搜索','完整合同评分卡','PDF报告及条款对比','已保存条款库','加密保险库（1,200份文件）'],
    yr_btn: '开通Plus年付',
    max_name: 'Max', max_badge: '专业用户', max_sub: '完整工具套装',
    max_f: ['包含Plus全部功能','IP交叉引用（USPTO）','3个Telegram AI机器人','交易结构模板','新文件条款提醒','条款版本追踪'],
    max_btn: '加入候补名单',
    price_note: '所有价格以新加坡元（SGD）计算（另有标注除外）。Max方案包含IP交叉引用功能，单独价值高达美元5,000/年。',
    sec_tag: '安全保障', sec_h2: '为重视隐私的律师而建',
    sec_desc: '您的研究内容属于机密。icontracts.net的每一层架构均为此而设计。',
    sec_f: [['AES-256-GCM','军事级加密标准。每份文件均采用源自您密码的唯一密钥加密。'],['无追踪','我们不追踪您的搜索记录，不出售数据，不以您的查询训练AI。'],['密钥仅您持有','加密密钥源自您的密码。密码遗忘，文件永久消失。这是有意为之的设计。']],
    ds_tag: '数据来源', ds_h3: '我们的智能分析从何而来',
    ds_desc: '我们从上述所有来源汇聚合同、公告和协议，进行向量化处理，分类为11个条款类别，按司法管辖区标注，并跨交易所交叉引用——让您能够比较同一条款类型在美国法律、新加坡法律和中国法律下的起草方式。所有内容均公开可得，均可核实，专注于公司法领域。',
    cta_h2: '探索您的条款真实市场位置。',
    cta_desc: '粘贴任意条款，即时获取对标338,000+份真实文件的评级。免费开始，无需信用卡。',
    cta_btn: '创建免费账户',
    ft_desc: '加密法律合同智能分析。搜索338,000+份真实商业合同并对标条款。',
    ft_product: '产品', ft_legal: '法律',
    ft_links: ['功能特点','价格方案','安全保障'], ft_legal_links: ['服务条款','隐私政策','联系我们'],
    ft_solo: 'icontracts.net由个人创始人构建和运营。支持查询将在48小时内回复。如需账单变更，请使用Stripe客户门户——这是最快捷的方式。',
    ft_rights: 'icontracts.net. 保留所有权利。',
    ft_disclaimer: '数据来源于公开归档的商业合同及监管披露文件。不构成法律意见。',
    wl_tag: 'Max候补名单', wl_h3: 'Max早期访问权限',
    wl_already: '您已在候补名单中。', wl_added: '您已加入候补名单。',
    wl_ok_desc: 'Max一上线，我们将立即发送邮件通知您。早期访问权限保留给上线时订阅Max的用户。',
    wl_close: '关闭',
    wl_desc: 'Max方案——包含IP交叉引用（USPTO）、Telegram AI机器人、交易结构模板和条款提醒——正在积极开发中。加入候补名单，我们将在准备就绪后第一时间与您联系。早期访问权限保留给上线时订阅Max的用户。',
    wl_name: '您的姓名', wl_name_ph: '请输入姓名',
    wl_email: '个人邮箱', wl_email_ph: 'name@gmail.com',
    wl_email_note: '请使用个人邮箱，而非律所邮箱。我们不想知道您就职于哪家律所。',
    wl_submit: '加入候补名单', wl_submitting: '提交中…',
    wl_no_pay: '无需付款，无需承诺。Max准备就绪时我们将发送邮件通知您。',
    wl_both: '请填写所有字段。',
    features_arr: [
      { title: '条款对标', desc: '粘贴您的条款，即时获取评级。精准了解您的语言与财富500强及上市公司实际归档内容的差距——更强、更弱或符合市场标准，均有数据佐证。', detail: 'A/B/C/D评级，附来源引用' },
      { title: '条款发现', desc: '搜索美国、新加坡和中国真实公司对任意条款类型的处理方式。赔偿上限、竞业禁止、不可抗力、准据法——均来自真实归档文件，而非模板。', detail: '覆盖3个司法管辖区的338,000+条款' },
      { title: '完整合同评分卡', desc: '上传完整合同，我们将其拆分为各条款逐一对标，精确呈现哪些内容强劲、哪些薄弱及原因——并对照市场实践核查具体条款。', detail: '逐条款评级及说明' },
      { title: '条款并排对比', desc: '将您的条款与语料库中最佳匹配内容并排展示，以红线标注方式高亮差异。精确了解与市场标准相比，哪些语言缺失、新增或有所不同。', detail: '可视化红线对比' },
      { title: '已保存条款库', desc: '收藏条款，按文件夹整理，添加个人备注。在职业生涯中构建专属动态智能库——每次使用平台，您的先例数据库都在持续成长。', detail: '个人先例数据库' },
      { title: '对标报告（PDF）', desc: '下载专业PDF报告，包含评级、评估、优势、劣势及可比条款。作为客户讨论和内部备忘录的参考文件。', detail: '可下载的分析报告' },
      { title: 'VIE架构库', desc: '美国上市中国公司的全部VIE组成文件——独家服务协议、股权质押、购股权、授权委托书。解锁中国限制行业准入的架构体系。', detail: '跨境交易先例' },
      { title: '准据法导航', desc: '针对多层跨境交易，了解真实归档交易如何在各层级选择准据法和争议解决机制。掌握SIAC、CIETAC还是AAA才是市场主流选择。', detail: '司法管辖区情报' },
      { title: '交易结构模板', desc: '常见交易类型的预制条款包——新中合资、VIE收购、跨境并购。了解真实交易如何从准据法到终止条款构建每一个条款。', detail: '基于真实归档文件的完整框架', anchor: 'feature-deal-templates' },
      { title: 'IP交叉引用（USPTO）', desc: '粘贴您的IP赔偿、许可或技术转让条款，即时关联相关美国专利。在起草陈述条款前，掌握哪些技术领域承载最高第三方风险。基于完整USPTO语料库。', detail: '条款对专利风险映射', anchor: 'feature-ip-cross-reference' },
      { title: '条款提醒与版本追踪', desc: '针对特定条款主题或关键词创建关注列表。当语料库中出现匹配语言的新文件时，我们将及时推送。追踪您执业领域的市场起草方式演变趋势。', detail: '持续市场情报', anchor: 'feature-clause-alerts' },
      { title: '加密保险库', desc: '以AES-256-GCM加密存储最多1,200份合同。每份文件均使用源自您密码的唯一密钥加密。您的研究成果永远属于您。', detail: '静态加密文件存储' },
    ],
    uc_arr: [
      { quote: '我粘贴了赔偿条款，获得A级评分，与财富500强SEC文件中的条款相似度达86%。现在当客户问"这是市场标准吗"，我有数据作证。', type: '条款对标' },
      { quote: '找到了12份美国上市中国科技公司的VIE独家服务协议，并排比较了购股权结构。为团队节省了3天的EDGAR检索时间。', type: '中国市场准入' },
      { quote: '对方律师坚持CIETAC仲裁。我向其展示，SEC语料库中72%的类似新中合资案例选择了SIAC。对方随即退让。', type: '谈判筹码' },
      { quote: '跨境并购交割前需要对标美国市场实践中的责任限制条款。30秒内得到答案——B级，略低于市场标准。', type: '美国资本市场' },
      { quote: '对比了同一交易结构中，新加坡法律合同与中国法律合同对不可抗力的处理方式。起草方式的差异令人深思。', type: '跨司法管辖区比较' },
      { quote: '合伙人要求为一位中国区总经理提供"市场标准"竞业禁止条款。我检索语料库，找到3,400+份高管雇用协议，并从最优示例中起草。', type: '高管雇用' },
    ],
    ds_arr: [
      { exchange: 'SEC EDGAR', jurisdiction: '美国', count: '338,000+', desc: '美国上市公司文件中的企业合同——10-K、10-Q、20-F、S-1附件' },
      { exchange: 'SGX', jurisdiction: '新加坡', count: '', desc: '新加坡交易所企业公告、重大合同及IPO招股说明书' },
      { exchange: 'HKEX', jurisdiction: '香港', count: '', desc: '香港交易所企业文件、上市文件及跨境协议' },
      { exchange: 'SSE', jurisdiction: '上海', count: '', desc: '上海证券交易所公开归档合同及企业披露文件' },
      { exchange: 'SZSE', jurisdiction: '深圳', count: '', desc: '深圳证券交易所文件——科技、制造业及跨境协议' },
      { exchange: '公开记录', jurisdiction: '多司法管辖区', count: '', desc: '监管披露、政府公报及可公开获取的法律数据库' },
    ],
    corpus_bars: [
      { label: '赔偿条款', count: '48,200+', pct: 82 },
      { label: '责任限制', count: '41,600+', pct: 71 },
      { label: '准据法', count: '38,900+', pct: 66 },
      { label: '终止条款', count: '35,100+', pct: 60 },
      { label: '保密条款', count: '29,800+', pct: 51 },
      { label: '不可抗力', count: '12,400+', pct: 21 },
      { label: '争议解决', count: '3,800+', pct: 7 },
    ],
    tg_voice1: '"我的赔偿上限与市场标准相比如何？"',
    tg_bot1a: '您的赔偿条款评分为', tg_bot1b: 'A级', tg_bot1c: '——与顶级SEC文件相似度86%。',
    tg_bot1d: '最相似：', tg_bot1e: '财富500强供应协议，附件10.1', tg_bot1f: '——上限结构与合同总价完全一致。',
    tg_bot1g: '强于语料库中78%的赔偿条款。',
    tg_voice2: '"展示本交易结构中SIAC与CIETAC的对比"',
    tg_bot2a: '对于新中合资结构，', tg_bot2b: 'SIAC被采用比例为72%', tg_bot2c: '，CIETAC为18%。',
    tg_bot2d: '来源：3份SEC 20-F文件，2份SGX公告',
  },
};

function FadeIn({ children, className = '', delay = 0 }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.12 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={ref} className={`transition-all duration-700 ease-out ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'} ${className}`}
      style={{ transitionDelay: visible ? `${delay}ms` : '0ms' }}>
      {children}
    </div>
  );
}

function MaxWaitlistModal({ onClose, t }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) { setError(t.wl_both); return; }
    setLoading(true); setError('');
    try {
      const res = await fetch('/api/waitlist/max', { method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include', body: JSON.stringify({ name: name.trim(), email: email.trim() }) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Could not save.');
      setSuccess(data.status || 'added');
    } catch (err) { setError(err.message || 'Something went wrong. Please try again.'); }
    finally { setLoading(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4 bg-slate-950/80 backdrop-blur-sm" onClick={onClose}>
      <div className="w-full max-w-md bg-slate-900 border border-slate-800 rounded-xl p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <p className="text-xs uppercase tracking-[0.15em] text-amber-500 font-medium mb-1">{t.wl_tag}</p>
            <h3 className="text-xl font-bold text-white">{t.wl_h3}</h3>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-white transition-colors"><XIcon className="w-5 h-5" /></button>
        </div>
        {success ? (
          <div className="py-4">
            <p className="text-amber-400 font-medium mb-2">{success === 'already_on_list' ? t.wl_already : t.wl_added}</p>
            <p className="text-sm text-slate-400 leading-relaxed">{t.wl_ok_desc}</p>
            <button onClick={onClose} className="mt-6 w-full py-2.5 bg-amber-600 hover:bg-amber-500 text-white font-medium rounded-md transition-all">{t.wl_close}</button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <p className="text-sm text-slate-400 leading-relaxed mb-5">{t.wl_desc}</p>
            <label className="block text-xs uppercase tracking-[0.1em] text-slate-500 font-medium mb-1.5">{t.wl_name}</label>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} autoFocus required maxLength={120} className="w-full px-3 py-2.5 mb-4 bg-slate-950 border border-slate-800 rounded-md text-white text-sm focus:outline-none focus:border-amber-600/50 focus:ring-1 focus:ring-amber-600/30" placeholder={t.wl_name_ph} />
            <label className="block text-xs uppercase tracking-[0.1em] text-slate-500 font-medium mb-1.5">{t.wl_email}</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required maxLength={254} className="w-full px-3 py-2.5 mb-2 bg-slate-950 border border-slate-800 rounded-md text-white text-sm focus:outline-none focus:border-amber-600/50 focus:ring-1 focus:ring-amber-600/30" placeholder={t.wl_email_ph} />
            <p className="text-xs text-slate-500 mb-4 leading-relaxed">{t.wl_email_note}</p>
            {error && <p className="text-sm text-red-400 mb-3">{error}</p>}
            <button type="submit" disabled={loading} className="w-full py-2.5 bg-amber-600 hover:bg-amber-500 disabled:bg-amber-800 text-white font-medium rounded-md transition-all active:scale-[0.98]">{loading ? t.wl_submitting : t.wl_submit}</button>
            <p className="text-xs text-slate-600 text-center mt-3">{t.wl_no_pay}</p>
          </form>
        )}
      </div>
    </div>
  );
}

function LiveSearch({ t }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setLoading(true); setHasSearched(true);
    try {
      const res = await fetch(`${API_BASE}/preview-search`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ query }) });
      const data = await res.json();
      setResults(data);
    } catch { setResults(null); }
    setLoading(false);
  };

  const placeholders = ['limitation of liability excluding indirect damages', 'force majeure pandemic exclusion', 'non-compete radius executive employment', 'change of control acceleration vesting', 'indemnification cap aggregate liability'];
  const [placeholder] = useState(placeholders[Math.floor(Math.random() * placeholders.length)]);

  return (
    <div className="w-full max-w-3xl mx-auto">
      <form onSubmit={handleSearch} className="relative">
        <div className="absolute inset-0 rounded-lg bg-amber-500/[0.06] blur-xl pointer-events-none" />
        <input value={query} onChange={e => setQuery(e.target.value)} placeholder={placeholder} className="relative w-full px-5 py-4 pr-32 bg-slate-800/80 border border-slate-700/60 rounded-lg text-white text-base placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-amber-500/30 focus:border-amber-600/40 transition-all" />
        <button type="submit" disabled={loading} className="absolute right-1.5 top-1/2 -translate-y-1/2 px-5 py-2 bg-amber-600 hover:bg-amber-500 disabled:bg-amber-800 text-white text-sm font-medium rounded-md transition-all active:scale-[0.97]">
          {loading ? t.search_loading : t.search_btn}
        </button>
      </form>
      {hasSearched && results && (
        <div className="mt-3 space-y-2 relative z-10">
          {results.results?.map((r, i) => (
            <div key={r.clause_id || i} className="bg-slate-800/90 backdrop-blur-md border border-slate-700/40 rounded-lg p-4 card-hover">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-medium px-2 py-0.5 rounded bg-slate-700/80 text-slate-300">{r.category?.replace(/_/g, ' ')}</span>
                <span className="text-xs text-slate-500">SEC Filing</span>
                <div className="flex items-center gap-1.5 ml-auto">
                  <div className="w-12 h-1 bg-slate-700 rounded-full overflow-hidden">
                    <div className="h-full bg-amber-500/60 rounded-full" style={{ width: `${Math.min(r.score * 100, 100)}%` }} />
                  </div>
                  <span className="text-xs text-slate-400 tabular-nums">{Math.round(r.score * 100)}%</span>
                </div>
              </div>
              <p className="text-sm text-slate-300 leading-relaxed">{r.preview}</p>
            </div>
          ))}
          <p className="text-center text-sm text-slate-500 mt-3">{t.search_cta}</p>
        </div>
      )}
    </div>
  );
}

export default function LandingPage({ onNavigateToAuth }) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [lang, setLang] = useState('en');
  const t = T[lang];
  const serif = { fontFamily: "'Playfair Display', Georgia, serif" };
  const toggleLang = () => setLang(l => l === 'en' ? 'zh' : 'en');

  return (
    <div className="min-h-screen bg-slate-950 text-white">

      {/* Nav */}
      <nav className="fixed top-0 w-full z-50 bg-slate-950/90 backdrop-blur-lg border-b border-slate-800/50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="#top" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="cursor-pointer" aria-label="Back to top"><Logo /></a>
          <div className="flex items-center gap-6">
            <a href="#features" className="text-sm text-slate-400 hover:text-white transition-colors hidden sm:block">{t.nav_features}</a>
            <a href="#pricing" className="text-sm text-slate-400 hover:text-white transition-colors hidden sm:block">{t.nav_pricing}</a>
            <a href="#security" className="text-sm text-slate-400 hover:text-white transition-colors hidden sm:block">{t.nav_security}</a>
            <button onClick={toggleLang} className="hidden sm:block text-xs font-medium px-2.5 py-1.5 border border-slate-700 hover:border-slate-500 text-slate-400 hover:text-white rounded-md transition-all">{t.lang_toggle}</button>
            <button onClick={onNavigateToAuth} className="px-4 py-2 bg-amber-600 hover:bg-amber-500 text-white text-sm font-medium rounded-md transition-all active:scale-[0.97] hidden sm:block">{t.nav_signin}</button>
            <button className="sm:hidden p-2 text-slate-400 hover:text-white" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
              {mobileMenuOpen ? <XIcon className="w-5 h-5" /> : <MenuIcon className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {mobileMenuOpen && (
          <div className="sm:hidden px-6 py-4 space-y-3 bg-slate-950/95 backdrop-blur-lg border-t border-slate-800/50">
            <a href="#features" onClick={() => setMobileMenuOpen(false)} className="block text-sm text-slate-400 hover:text-white py-1">{t.nav_features}</a>
            <a href="#pricing" onClick={() => setMobileMenuOpen(false)} className="block text-sm text-slate-400 hover:text-white py-1">{t.nav_pricing}</a>
            <a href="#security" onClick={() => setMobileMenuOpen(false)} className="block text-sm text-slate-400 hover:text-white py-1">{t.nav_security}</a>
            <button onClick={toggleLang} className="w-full px-4 py-2 border border-slate-700 text-slate-400 text-sm font-medium rounded-md transition-all text-left">{t.lang_toggle}</button>
            <button onClick={() => { setMobileMenuOpen(false); onNavigateToAuth(); }} className="w-full px-4 py-2.5 bg-amber-600 hover:bg-amber-500 text-white text-sm font-medium rounded-md transition-all">{t.nav_signin}</button>
          </div>
        )}
      </nav>

      {/* Hero */}
      <section className="pt-36 pb-20 px-6 hero-gradient relative">
        <div className="absolute inset-0 bg-dot-pattern opacity-40 pointer-events-none" />
        <div className="max-w-4xl mx-auto text-center relative">
          <FadeIn>
            <p className="text-xs uppercase tracking-[0.2em] text-amber-500/80 font-medium mb-6">{t.hero_tag}</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.1] mb-6 tracking-tight" style={serif}>
              {t.hero_h1a}<span className="text-amber-500">{t.hero_h1b}</span>{t.hero_h1c}
            </h1>
            <p className="text-base sm:text-lg text-slate-300 max-w-2xl mx-auto mb-5 leading-relaxed">{t.hero_desc}</p>
            <p className="text-sm text-slate-400 max-w-xl mx-auto mb-14">{t.hero_sub}</p>
          </FadeIn>
          <FadeIn delay={150}><LiveSearch t={t} /></FadeIn>
        </div>
      </section>

      {/* Social proof */}
      <section className="py-8 border-y border-slate-800/40">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex flex-wrap justify-center items-center gap-x-12 gap-y-4">
            <div className="flex items-center gap-3"><span className="text-2xl font-bold text-white tabular-nums">338,439</span><span className="text-xs text-slate-500 leading-tight">{t.proof_clauses[0]}<br />{t.proof_clauses[1]}</span></div>
            <div className="w-px h-8 bg-slate-800 hidden sm:block" />
            <div className="flex items-center gap-3"><span className="text-2xl font-bold text-white tabular-nums">3</span><span className="text-xs text-slate-500 leading-tight">{t.proof_j[0]}<br />{t.proof_j[1]}</span></div>
            <div className="w-px h-8 bg-slate-800 hidden sm:block" />
            <div className="flex items-center gap-3"><span className="text-2xl font-bold text-white tabular-nums">11</span><span className="text-xs text-slate-500 leading-tight">{t.proof_cat[0]}<br />{t.proof_cat[1]}</span></div>
            <div className="w-px h-8 bg-slate-800 hidden sm:block" />
            <div className="flex items-center gap-3"><span className="text-2xl font-bold text-white">AES-256</span><span className="text-xs text-slate-500 leading-tight">{t.proof_enc[0]}<br />{t.proof_enc[1]}</span></div>
          </div>
        </div>
      </section>

      {/* Problem */}
      <section className="py-24 px-6 relative">
        <div className="absolute inset-0 bg-dot-pattern opacity-20 pointer-events-none" />
        <FadeIn>
          <div className="max-w-5xl mx-auto">
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <div>
                <p className="text-xs uppercase tracking-[0.15em] text-slate-500 font-medium mb-5">{t.prob_tag}</p>
                <blockquote className="text-2xl sm:text-3xl font-bold leading-snug mb-6" style={serif}>
                  {t.prob_q1}<span className="text-amber-500">{t.prob_qhl}</span>{t.prob_q2}
                </blockquote>
                <p className="text-slate-300 leading-relaxed mb-4">{t.prob_p1}</p>
                <p className="text-slate-300 leading-relaxed mb-8">{t.prob_p2}</p>
                <button onClick={onNavigateToAuth} className="px-5 py-2.5 bg-amber-600 hover:bg-amber-500 text-white text-sm font-medium rounded-md transition-all active:scale-[0.97]">{t.prob_btn}</button>
              </div>
              <div className="space-y-4">
                <div className="bg-slate-900/60 border border-slate-800/60 rounded-lg p-5">
                  <p className="text-xs text-slate-500 uppercase tracking-wide mb-4">{t.corpus_title}</p>
                  {t.corpus_bars.map((item, i) => (
                    <div key={i} className="flex items-center gap-3 mb-2.5 last:mb-0">
                      <span className="text-xs text-slate-400 w-36 shrink-0">{item.label}</span>
                      <div className="flex-1 h-1.5 bg-slate-800 rounded-full overflow-hidden"><div className="h-full bg-slate-600 rounded-full transition-all duration-700" style={{ width: `${item.pct}%` }} /></div>
                      <span className="text-xs text-slate-500 tabular-nums w-16 text-right">{item.count}</span>
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {[['338,439','US (SEC EDGAR)'],['1,213','Singapore (SGX)'],['884','China-tagged']].map(([n,l],i) => (
                    <div key={i} className="bg-slate-900/60 border border-slate-800/60 rounded-lg p-4 text-center"><p className="text-lg font-bold text-white tabular-nums">{n}</p><p className="text-xs text-slate-500 mt-0.5">{l}</p></div>
                  ))}
                </div>
                <div className="grid grid-cols-3 gap-3">
                  {[['880+','Hong Kong (HKEX)'],['3,804',lang==='en'?'Arbitration clauses':'仲裁条款'],['11',lang==='en'?'Clause categories':'条款类别']].map(([n,l],i) => (
                    <div key={i} className="bg-slate-900/60 border border-slate-800/60 rounded-lg p-4 text-center"><p className="text-lg font-bold text-white tabular-nums">{n}</p><p className="text-xs text-slate-500 mt-0.5">{l}</p></div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </FadeIn>
      </section>

      {/* AI client */}
      <section className="py-24 px-6 bg-slate-950/60 border-y border-slate-800/40">
        <div className="max-w-3xl mx-auto">
          <FadeIn>
            <p className="text-xs uppercase tracking-[0.15em] text-amber-500 font-medium text-center mb-3">{t.ai_tag}</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-white text-center mb-6 leading-tight" style={serif}>{t.ai_h2}</h2>
            <p className="text-slate-300 text-center leading-relaxed mb-5 text-lg">{t.ai_p1a}<span className="text-white">{t.ai_p1b}</span>{t.ai_p1c}</p>
            <p className="text-slate-400 text-center leading-relaxed mb-5">{t.ai_p2}</p>
            <p className="text-slate-400 text-center leading-relaxed mb-8">{t.ai_p3}</p>
            <p className="text-center"><span className="text-base text-amber-400 italic font-medium">{t.ai_quote}</span></p>
          </FadeIn>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="py-24 px-6 section-fade">
        <div className="max-w-5xl mx-auto">
          <FadeIn>
            <p className="text-xs uppercase tracking-[0.15em] text-slate-500 font-medium text-center mb-3">{t.feat_tag}</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-3" style={serif}>{t.feat_h2}</h2>
            <p className="text-slate-400 text-center mb-16 max-w-2xl mx-auto">{t.feat_desc}</p>
          </FadeIn>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {t.features_arr.map((item, i) => {
              const icons = [ScaleIcon,SearchIcon,ClipboardCheckIcon,BookIcon,BookIcon,CompassIcon,BookIcon,CompassIcon,ScaleIcon,SearchIcon,CompassIcon,LockIcon];
              const Icon = icons[i] || BookIcon;
              return (
                <FadeIn key={i} delay={i * 80}>
                  <div id={item.anchor} className="bg-slate-900/40 border border-slate-800/50 rounded-lg p-6 card-hover group flex flex-col scroll-mt-24">
                    <div className="w-10 h-10 bg-slate-800/80 border border-slate-700/50 rounded-md flex items-center justify-center mb-4 group-hover:border-slate-600/50 transition-colors">
                      <Icon className="w-5 h-5 text-amber-500/70 icon-shimmer" />
                    </div>
                    <h3 className="text-base font-semibold text-white mb-2">{item.title}</h3>
                    <p className="text-sm text-slate-400 leading-relaxed mb-4 flex-1">{item.desc}</p>
                    <p className="text-xs text-slate-500 font-medium">{item.detail}</p>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* Use cases */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <FadeIn>
            <p className="text-xs uppercase tracking-[0.15em] text-slate-500 font-medium text-center mb-3">{t.uc_tag}</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-4" style={serif}>{t.uc_h2}</h2>
            <p className="text-slate-400 text-center mb-16 max-w-2xl mx-auto">{t.uc_desc}</p>
          </FadeIn>
          <div className="grid sm:grid-cols-2 gap-4">
            {t.uc_arr.map((item, i) => (
              <FadeIn key={i} delay={i * 80}>
                <div className="bg-slate-900/30 border border-slate-800/40 rounded-lg p-5 card-hover">
                  <p className="text-sm text-slate-300 leading-relaxed mb-4">"{item.quote}"</p>
                  <span className="text-xs font-medium px-2 py-1 rounded bg-slate-800/60 text-slate-500">{item.type}</span>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* Diff */}
      <section className="py-24 px-6 section-fade">
        <div className="max-w-6xl mx-auto">
          <FadeIn>
            <div className="text-center mb-12">
              <p className="text-xs uppercase tracking-[0.15em] text-amber-500/80 font-medium mb-5">{t.diff_tag}</p>
              <h2 className="text-2xl sm:text-3xl font-bold leading-snug mb-4" style={serif}>{t.diff_h2a}<span className="text-amber-500 text-shimmer">{t.diff_h2b}</span></h2>
              <p className="text-slate-400 max-w-2xl mx-auto">{t.diff_desc}</p>
            </div>
          </FadeIn>
          <FadeIn delay={200}>
            <div className="bg-slate-800 border border-slate-700/60 rounded-lg shadow-2xl overflow-hidden">
              <div className="p-5 border-b border-slate-700/40">
                <h3 className="text-base font-semibold text-white">{lang === 'en' ? 'Side-by-Side Clause Comparison' : '条款并排对比'}</h3>
                <p className="text-xs text-slate-400 mt-1">{lang === 'en' ? 'Differences highlighted · 78% match · Indemnity' : '差异高亮显示 · 78%匹配度 · 赔偿条款'}</p>
              </div>
              <div className="px-5 py-2 flex items-center gap-4 border-b border-slate-700/20">
                {[[`bg-red-900/40 border-red-800/40`, t.diff_leg1],[`bg-green-900/40 border-green-800/40`, t.diff_leg2],[`bg-slate-700/40`, t.diff_leg3]].map(([cls, label], i) => (
                  <span key={i} className="flex items-center gap-1.5 text-xs"><span className={`w-3 h-3 rounded ${cls} border`} /><span className="text-slate-400">{label}</span></span>
                ))}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
                <div className="p-5 border-r border-slate-700/30">
                  <div className="flex items-center gap-2 mb-4"><span className="text-xs font-medium px-2.5 py-1 rounded bg-amber-900/30 text-amber-300 border border-amber-800/30">{t.diff_yours}</span></div>
                  <p className="text-sm leading-relaxed">
                    <span className="text-slate-300">{lang==='en'?'The Seller shall indemnify and hold harmless the Buyer ':'卖方应赔偿并使买方'}</span>
                    <span className="bg-red-900/30 text-red-300 px-0.5 rounded">{lang==='en'?'and its affiliates, officers, and directors ':'及其关联方、高管和董事'}</span>
                    <span className="text-slate-300">{lang==='en'?'against any and all losses, claims, damages, ':'免受任何及所有损失、索赔、损害'}</span>
                    <span className="bg-red-900/30 text-red-300 px-0.5 rounded">{lang==='en'?'liabilities, and reasonable expenses ':'、负债及合理费用'}</span>
                    <span className="text-slate-300">{lang==='en'?'arising out of or resulting from any breach of ':'因以下违约而产生或由此导致的：'}</span>
                    <span className="bg-red-900/30 text-red-300 px-0.5 rounded">{lang==='en'?'any representation, warranty, or covenant ':'任何陈述、保证或承诺'}</span>
                    <span className="text-slate-300">{lang==='en'?'made by the Seller in this Agreement, ':'卖方在本协议中所作的，'}</span>
                    <span className="bg-red-900/30 text-red-300 px-0.5 rounded">{lang==='en'?'subject to the limitations set forth in Section 8.4.':'须受第8.4条所列限制约束。'}</span>
                  </p>
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-4"><span className="text-xs font-medium px-2.5 py-1 rounded bg-blue-900/30 text-blue-300 border border-blue-800/30">{t.diff_match}</span></div>
                  <p className="text-sm leading-relaxed">
                    <span className="text-slate-300">{lang==='en'?'The Seller shall indemnify and hold harmless the Buyer ':'卖方应赔偿并使买方'}</span>
                    <span className="bg-green-900/30 text-green-300 px-0.5 rounded">{lang==='en'?'and each of its subsidiaries, successors, and assigns ':'及其各子公司、继承人和受让方'}</span>
                    <span className="text-slate-300">{lang==='en'?'against any and all losses, claims, damages, ':'免受任何及所有损失、索赔、损害'}</span>
                    <span className="bg-green-900/30 text-green-300 px-0.5 rounded">{lang==='en'?'costs, fees, penalties, and expenses (including reasonable attorneys\' fees) ':'、费用、罚款及开支（含合理律师费）'}</span>
                    <span className="text-slate-300">{lang==='en'?'arising out of or resulting from any breach of ':'因以下违约而产生或由此导致的：'}</span>
                    <span className="bg-green-900/30 text-green-300 px-0.5 rounded">{lang==='en'?'any representation or warranty ':'任何陈述或保证'}</span>
                    <span className="text-slate-300">{lang==='en'?'made by the Seller in this Agreement, ':'卖方在本协议中所作的，'}</span>
                    <span className="bg-green-900/30 text-green-300 px-0.5 rounded">{lang==='en'?'provided that the aggregate liability shall not exceed the Purchase Price.':'前提是累计责任不得超过购买价格。'}</span>
                  </p>
                </div>
              </div>
              <div className="px-5 py-3 border-t border-slate-700/40"><p className="text-xs text-slate-500">{t.diff_src}</p></div>
            </div>
          </FadeIn>
          <FadeIn delay={400}>
            <div className="mt-8 text-center">
              <p className="text-sm text-slate-400 mb-6">{t.diff_sub}</p>
              <button onClick={onNavigateToAuth} className="px-8 py-3 bg-amber-600 hover:bg-amber-500 text-white text-sm font-semibold rounded-lg transition-all active:scale-[0.97] shadow-lg shadow-amber-900/20">{t.diff_btn}</button>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* Telegram */}
      <section id="feature-telegram-bots" className="py-24 px-6 section-fade scroll-mt-24">
        <div className="max-w-5xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <FadeIn>
              <div>
                <p className="text-xs uppercase tracking-[0.15em] text-amber-500/80 font-medium mb-5">{t.tg_tag}</p>
                <h2 className="text-2xl sm:text-3xl font-bold leading-snug mb-6" style={serif}>{t.tg_h2a}<span className="text-amber-500 text-shimmer">{t.tg_h2b}</span>{t.tg_h2c}</h2>
                <p className="text-slate-300 leading-relaxed mb-4">{t.tg_p1}</p>
                <p className="text-slate-300 leading-relaxed mb-4">{t.tg_p2}</p>
                <p className="text-slate-400 text-sm leading-relaxed mb-6">{t.tg_p3}</p>
                <p className="text-[0.7rem] text-slate-500">{t.tg_plan}</p>
              </div>
            </FadeIn>
            <FadeIn delay={200}>
              <div className="flex justify-center">
                <div className="w-72 bg-slate-900 rounded-[2rem] border border-slate-700/50 p-3 shadow-2xl relative">
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-24 h-5 bg-slate-900 rounded-b-2xl z-10" />
                  <div className="bg-slate-800 rounded-[1.5rem] p-4 space-y-3 min-h-[420px] overflow-hidden">
                    <div className="flex items-center gap-2 pb-3 border-b border-slate-700/40">
                      <div className="w-8 h-8 bg-amber-600 rounded-full flex items-center justify-center text-white text-xs font-bold">ic</div>
                      <div><p className="text-sm font-semibold text-white">icontracts Benchmark Bot</p><p className="text-xs text-green-400">online</p></div>
                    </div>
                    <div className="flex justify-end">
                      <div className="bg-amber-600/20 border border-amber-600/30 rounded-lg rounded-br-sm px-3 py-2 max-w-[85%]">
                        <div className="flex items-center gap-2 mb-1">
                          <svg className="w-3.5 h-3.5 text-amber-400" viewBox="0 0 24 24" fill="currentColor"><path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/><path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/></svg>
                          <div className="flex gap-0.5 items-center">{[3,5,2,7,4,6,3,8,5,3,6,4,7,2,5,3].map((h,i) => <div key={i} className="w-0.5 bg-amber-400/60 rounded-full" style={{height:h*2+'px'}} />)}</div>
                          <span className="text-xs text-amber-400/60">0:04</span>
                        </div>
                        <p className="text-xs text-amber-200/80 italic">{t.tg_voice1}</p>
                      </div>
                    </div>
                    <div className="flex items-end gap-2">
                      <div className="w-6 h-6 bg-amber-600 rounded-full flex items-center justify-center text-white text-[8px] font-bold shrink-0">ic</div>
                      <div className="bg-slate-700/50 rounded-lg rounded-bl-sm px-3 py-2 max-w-[85%]">
                        <p className="text-xs text-slate-300 mb-2">{t.tg_bot1a}<span className="text-amber-400 font-semibold">{t.tg_bot1b}</span>{t.tg_bot1c}</p>
                        <p className="text-xs text-slate-400 mb-2">{t.tg_bot1d}<span className="text-slate-300">{t.tg_bot1e}</span>{t.tg_bot1f}</p>
                        <p className="text-xs text-slate-500">{t.tg_bot1g}</p>
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <div className="bg-amber-600/20 border border-amber-600/30 rounded-lg rounded-br-sm px-3 py-2 max-w-[85%]">
                        <div className="flex items-center gap-2 mb-1">
                          <svg className="w-3.5 h-3.5 text-amber-400" viewBox="0 0 24 24" fill="currentColor"><path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/><path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/></svg>
                          <div className="flex gap-0.5 items-center">{[4,6,3,5,7,2,6,4,8,3,5,7].map((h,i) => <div key={i} className="w-0.5 bg-amber-400/60 rounded-full" style={{height:h*2+'px'}} />)}</div>
                          <span className="text-xs text-amber-400/60">0:03</span>
                        </div>
                        <p className="text-xs text-amber-200/80 italic">{t.tg_voice2}</p>
                      </div>
                    </div>
                    <div className="flex items-end gap-2">
                      <div className="w-6 h-6 bg-amber-600 rounded-full flex items-center justify-center text-white text-[8px] font-bold shrink-0">ic</div>
                      <div className="bg-slate-700/50 rounded-lg rounded-bl-sm px-3 py-2 max-w-[85%]">
                        <p className="text-xs text-slate-300 mb-1">{t.tg_bot2a}<span className="text-amber-400 font-semibold">{t.tg_bot2b}</span>{t.tg_bot2c}</p>
                        <p className="text-xs text-slate-500">{t.tg_bot2d}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <p className="text-center text-xs text-amber-500 text-shimmer mt-4">{t.tg_tag2}</p>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* Privacy */}
      <section className="py-16 px-6 border-y border-slate-800/40 bg-slate-950/40">
        <div className="max-w-3xl mx-auto text-center">
          <FadeIn>
            <p className="text-xs uppercase tracking-[0.15em] text-amber-500 font-medium mb-3">{t.priv_tag}</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4" style={serif}>{t.priv_h2}</h2>
            <p className="text-slate-400 leading-relaxed mb-4">{t.priv_p1a}<span className="text-amber-400">{t.priv_p1b}</span>{t.priv_p1c}</p>
            <p className="text-slate-400 leading-relaxed mb-4">{t.priv_p2a}<span className="text-white">{t.priv_p2b}</span>{t.priv_p2c}</p>
            <p className="text-sm text-slate-500 italic">{t.priv_quote}</p>
          </FadeIn>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-24 px-6 section-fade">
        <div className="max-w-6xl mx-auto">
          <FadeIn>
            <p className="text-xs uppercase tracking-[0.15em] text-slate-500 font-medium text-center mb-3">{t.price_tag}</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-center mb-3" style={serif}>{t.price_h2}</h2>
            <p className="text-slate-400 text-center mb-16">{t.price_desc}</p>
          </FadeIn>
          <div className="grid md:grid-cols-4 gap-4 items-start">
            <FadeIn>
              <div className="rounded-lg p-6 border border-slate-800/30 flex flex-col">
                <h3 className="text-base font-semibold text-slate-300 mb-1">{t.free_name}</h3>
                <p className="text-3xl font-bold text-slate-300 tabular-nums mb-1">$0</p>
                <p className="text-sm text-slate-500 mb-6">{t.free_sub}</p>
                <ul className="space-y-2.5 text-sm text-slate-500 mb-8 flex-1">
                  {t.free_f.map((f, i) => <li key={i} className="flex gap-2"><span className={i < 3 ? 'text-slate-600' : 'text-slate-700'}>{ i < 3 ? '✓' : '×'}</span><span className={i >= 3 ? 'text-slate-700' : ''}>{f}</span></li>)}
                </ul>
                <button onClick={onNavigateToAuth} className="w-full py-2.5 text-slate-400 hover:text-white border border-slate-700 hover:border-slate-600 font-medium rounded-md transition-all text-sm">{t.free_btn}</button>
              </div>
            </FadeIn>
            <FadeIn delay={80}>
              <div className="rounded-lg p-6 border border-slate-800/30 flex flex-col">
                <h3 className="text-base font-semibold text-white mb-1">{t.mo_name}</h3>
                <div className="mb-1"><span className="text-3xl font-bold text-white tabular-nums">S$90</span><span className="text-base font-normal text-slate-400">/mo</span></div>
                <p className="text-sm text-slate-500 mb-6">{t.mo_sub}</p>
                <ul className="space-y-2.5 text-sm text-slate-300 mb-8 flex-1">
                  {t.plus_f.map((f, i) => <li key={i} className="flex gap-2"><span className="text-amber-500">✓</span>{f}</li>)}
                </ul>
                <button onClick={onNavigateToAuth} className="w-full py-2.5 border border-amber-600/40 text-amber-400 hover:bg-amber-600/10 font-medium rounded-md transition-all text-sm">{t.mo_btn}</button>
              </div>
            </FadeIn>
            <FadeIn delay={160}>
              <div className="bg-slate-900/60 rounded-lg p-6 relative border border-amber-600/20 glow-amber flex flex-col">
                <div className="absolute -top-3 left-6 px-3 py-0.5 bg-amber-600 text-white text-xs font-medium rounded">{t.yr_badge}</div>
                <h3 className="text-base font-semibold text-white mb-1">{t.yr_name}</h3>
                <div className="mb-1"><span className="text-3xl font-bold text-white tabular-nums">S$30</span><span className="text-base font-normal text-slate-400">/mo</span></div>
                <p className="text-sm text-slate-500 mb-6">{t.yr_sub}</p>
                <ul className="space-y-2.5 text-sm text-slate-300 mb-8 flex-1">
                  {t.yr_f.map((f, i) => <li key={i} className="flex gap-2"><span className="text-amber-500">✓</span>{f}</li>)}
                </ul>
                <button onClick={onNavigateToAuth} className="w-full py-2.5 bg-amber-600 hover:bg-amber-500 text-white font-medium rounded-md transition-all active:scale-[0.98] text-sm">{t.yr_btn}</button>
              </div>
            </FadeIn>
            <FadeIn delay={240}>
              <div className="bg-slate-900/60 rounded-lg p-6 relative border border-slate-700/40 flex flex-col">
                <div className="absolute -top-3 left-6 px-3 py-0.5 bg-slate-700 text-amber-400 text-xs font-medium rounded">{t.max_badge}</div>
                <h3 className="text-base font-semibold text-white mb-1">{t.max_name}</h3>
                <div className="mb-1"><span className="text-3xl font-bold text-white tabular-nums">US$500</span><span className="text-base font-normal text-slate-400">/year</span></div>
                <p className="text-sm text-slate-500 mb-6">{t.max_sub}</p>
                <ul className="space-y-2.5 text-sm text-slate-300 mb-8 flex-1">
                  {t.max_f.map((f, i) => <li key={i} className="flex gap-2"><span className="text-amber-500">✓</span>{f}</li>)}
                </ul>
                <button onClick={() => setWaitlistOpen(true)} className="w-full py-2.5 bg-slate-700 hover:bg-slate-600 text-white font-medium rounded-md transition-all active:scale-[0.98] text-sm">{t.max_btn}</button>
              </div>
            </FadeIn>
          </div>
          <FadeIn delay={300}><p className="text-center text-xs text-slate-600 mt-8">{t.price_note}</p></FadeIn>
        </div>
      </section>

      {/* Security */}
      <section id="security" className="py-24 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <FadeIn>
            <p className="text-xs uppercase tracking-[0.15em] text-slate-500 font-medium mb-3">{t.sec_tag}</p>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={serif}>{t.sec_h2}</h2>
            <p className="text-slate-400 mb-14 leading-relaxed max-w-xl mx-auto">{t.sec_desc}</p>
          </FadeIn>
          <div className="grid sm:grid-cols-3 gap-5 text-left">
            {t.sec_f.map(([title, desc], i) => {
              const icons = [<path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />, <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></>, <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />];
              return (
                <FadeIn key={i} delay={i * 80}>
                  <div className="bg-slate-900/30 border border-slate-800/40 rounded-lg p-5 card-hover">
                    <div className="w-9 h-9 bg-slate-800/60 border border-slate-700/40 rounded-md flex items-center justify-center mb-4">
                      <svg className="w-4 h-4 text-amber-500/80 security-glow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">{icons[i]}</svg>
                    </div>
                    <h4 className="text-sm font-semibold text-white mb-2">{title}</h4>
                    <p className="text-xs text-slate-400 leading-relaxed">{desc}</p>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* Data sources */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <FadeIn>
            <p className="text-xs uppercase tracking-[0.15em] text-slate-600 font-medium text-center mb-3">{t.ds_tag}</p>
            <h3 className="text-xl font-semibold text-white text-center mb-8" style={serif}>{t.ds_h3}</h3>
          </FadeIn>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {t.ds_arr.map((s, i) => (
              <FadeIn key={i} delay={i * 60}>
                <div className="bg-slate-900/30 border border-slate-800/30 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-semibold text-white">{s.exchange}</span>
                    {s.count && <span className="text-xs tabular-nums text-amber-500/80 font-medium">{s.count}</span>}
                  </div>
                  <p className="text-xs text-slate-500 leading-relaxed">{s.desc}</p>
                </div>
              </FadeIn>
            ))}
          </div>
          <FadeIn><p className="text-sm text-slate-400 leading-relaxed text-center max-w-3xl mx-auto">{t.ds_desc}</p></FadeIn>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6">
        <FadeIn>
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={serif}>{t.cta_h2}</h2>
            <p className="text-slate-400 mb-10">{t.cta_desc}</p>
            <button onClick={onNavigateToAuth} className="px-7 py-3 bg-amber-600 hover:bg-amber-500 text-white text-base font-medium rounded-md transition-all active:scale-[0.97]">{t.cta_btn}</button>
          </div>
        </FadeIn>
      </section>

      {/* Footer */}
      <footer className="py-10 px-6 bg-slate-950 border-t border-slate-800/30">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col sm:flex-row gap-8 justify-between mb-8">
            <div className="max-w-xs">
              <a href="#top" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: 'smooth' }); }} className="cursor-pointer inline-block" aria-label="Back to top"><Logo className="mb-3" /></a>
              <p className="text-xs text-slate-600 leading-relaxed">{t.ft_desc}</p>
            </div>
            <div className="flex gap-12">
              <div>
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">{t.ft_product}</h4>
                <ul className="space-y-2 text-sm">
                  {[['#features','#pricing','#security'], t.ft_links].reduce((acc, _, i, arr) => i === 0 ? arr[0].map((href, j) => ({ href, label: arr[1][j] })) : acc, []).map((item, i) => (
                    <li key={i}><a href={item.href} className="text-slate-600 hover:text-slate-300 transition-colors">{item.label}</a></li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">{t.ft_legal}</h4>
                <ul className="space-y-2 text-sm">
                  {[['/terms','/privacy','mailto:support@icontracts.net'], t.ft_legal_links].reduce((acc, _, i, arr) => i === 0 ? arr[0].map((href, j) => ({ href, label: arr[1][j] })) : acc, []).map((item, i) => (
                    <li key={i}><a href={item.href} className="text-slate-600 hover:text-slate-300 transition-colors">{item.label}</a></li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
          <div className="pt-6 border-t border-slate-800/20 space-y-3">
            <p className="text-xs text-slate-600 text-center sm:text-left">{t.ft_solo}</p>
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-xs text-slate-700">&copy; {new Date().getFullYear()} {t.ft_rights}</p>
              <p className="text-xs text-slate-700">{t.ft_disclaimer}</p>
            </div>
          </div>
        </div>
      </footer>

      {waitlistOpen && <MaxWaitlistModal onClose={() => setWaitlistOpen(false)} t={t} />}
    </div>
  );
}
